import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  SPARTACUS_DEFAULT_RANGE_DAYS,
  SPARTACUS_MAX_DATE_RANGE_DAYS,
  SPARTACUS_TRACKING_START_DATE,
  SPARTACUS_UK_TIMEZONE,
} from 'src/constants/spartacus'

dayjs.extend(utc)
dayjs.extend(timezone)

function ukDayKey(dateInput) {
  if (dateInput == null || dateInput === '') return null
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dayjs.tz(dateInput, SPARTACUS_UK_TIMEZONE).isValid()
      ? dateInput
      : null
  }
  const parsed = dayjs.utc(dateInput).tz(SPARTACUS_UK_TIMEZONE)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

function shiftUkDay(key, days) {
  return dayjs
    .tz(key, SPARTACUS_UK_TIMEZONE)
    .add(days, 'day')
    .format('YYYY-MM-DD')
}

function ukDayStart(key) {
  const parsed = dayjs.tz(key, SPARTACUS_UK_TIMEZONE)
  return parsed.isValid() ? parsed : null
}

function ukDayEnd(key) {
  const next = ukDayStart(shiftUkDay(key, 1))
  if (!next) return null
  return dayjs(next.toDate().getTime() - 1)
}

function toUkDay(dateInput) {
  const key = ukDayKey(dateInput)
  return key ? ukDayStart(key) : null
}

/** Inclusive UK calendar-day range: today and the previous (days - 1) days. */
export function getDefaultSpartacusDateRange(now = dayjs()) {
  const instant = dayjs.isDayjs(now) ? now.toDate() : now
  const todayKey = ukDayKey(instant)
  const fromKey = shiftUkDay(todayKey, -(SPARTACUS_DEFAULT_RANGE_DAYS - 1))
  return {
    fromDate: ukDayStart(fromKey).toDate(),
    toDate: ukDayEnd(todayKey).toDate(),
  }
}

export function resolveSpartacusDateRange({
  fromDate,
  toDate,
  now = dayjs(),
} = {}) {
  if (!fromDate && !toDate) {
    return getDefaultSpartacusDateRange(now)
  }

  const fromKey = fromDate ? ukDayKey(fromDate) : null
  const toKey = toDate ? ukDayKey(toDate) : null

  if (fromDate && !fromKey) {
    throw new Error('fromDate is invalid')
  }
  if (toDate && !toKey) {
    throw new Error('toDate is invalid')
  }

  if (fromKey && toKey) {
    if (fromKey > toKey) {
      throw new Error('fromDate must be on or before toDate')
    }
    return {
      fromDate: ukDayStart(fromKey).toDate(),
      toDate: ukDayEnd(toKey).toDate(),
    }
  }

  if (fromKey && !toKey) {
    const resolvedTo = shiftUkDay(fromKey, SPARTACUS_DEFAULT_RANGE_DAYS - 1)
    return {
      fromDate: ukDayStart(fromKey).toDate(),
      toDate: ukDayEnd(resolvedTo).toDate(),
    }
  }

  const resolvedFrom = shiftUkDay(toKey, -(SPARTACUS_DEFAULT_RANGE_DAYS - 1))
  return {
    fromDate: ukDayStart(resolvedFrom).toDate(),
    toDate: ukDayEnd(toKey).toDate(),
  }
}

export function getSpartacusRangeDaySpan(fromDate, toDate) {
  const fromKey = ukDayKey(fromDate)
  const toKey = ukDayKey(toDate)

  if (!fromKey || !toKey) {
    return 0
  }

  return (
    dayjs
      .tz(toKey, SPARTACUS_UK_TIMEZONE)
      .diff(dayjs.tz(fromKey, SPARTACUS_UK_TIMEZONE), 'day') + 1
  )
}

export function isSpartacusDateRangeWithinLimit(fromDate, toDate) {
  return (
    getSpartacusRangeDaySpan(fromDate, toDate) <= SPARTACUS_MAX_DATE_RANGE_DAYS
  )
}

export function formatSpartacusUkDate(date) {
  const parsed = toUkDay(date)
  return parsed ? parsed.format('D MMM YYYY') : ''
}

export function formatSpartacusUkDateRange({ fromDate, toDate }) {
  return `${formatSpartacusUkDate(fromDate)} – ${formatSpartacusUkDate(toDate)}`
}

/** Previous period of equal duration immediately before fromDate (UK). */
export function getPriorEquivalentPeriod({ fromDate, toDate }) {
  const from = toUkDay(fromDate)
  const toKey = ukDayKey(toDate)
  const to = toKey ? ukDayEnd(toKey) : null

  if (!from || !to) {
    return {
      priorFromDate: null,
      priorToDate: null,
    }
  }

  const durationMs = to.diff(from)

  const priorTo = from.subtract(1, 'millisecond')
  const priorFrom = priorTo.subtract(durationMs, 'millisecond')

  return {
    priorFromDate: priorFrom.toDate(),
    priorToDate: priorTo.toDate(),
  }
}

export function formatSpartacusPriorDateRange({ fromDate, toDate }) {
  const prior = getPriorEquivalentPeriod({ fromDate, toDate })
  return formatSpartacusUkDateRange({
    fromDate: prior.priorFromDate,
    toDate: prior.priorToDate,
  })
}

export function getSpartacusPriorPeriodTooltip({ fromDate, toDate }) {
  const priorLabel = formatSpartacusPriorDateRange({ fromDate, toDate })
  const currentSpan = getSpartacusRangeDaySpan(fromDate, toDate)

  return `Trends and deltas compare your selected range to the prior equivalent period: the same ${currentSpan} day${
    currentSpan === 1 ? '' : 's'
  }, ending the day before your range starts (UK time). Right now that prior window is ${priorLabel}. This is not calendar month vs month — if you pick 10–20 Jun, we compare to the 11 days immediately before 10 Jun.`
}

export function formatSpartacusTrackingStartDate() {
  return formatSpartacusUkDate(SPARTACUS_TRACKING_START_DATE)
}

export function getSpartacusLastSeenTooltip() {
  return `We started tracking last seen on ${formatSpartacusTrackingStartDate()}. Accounts that have not logged in since then will show Not tracked yet.`
}
